import { useState, useEffect } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getOrgTypeTopicTypes,
  getTopicTypes,
  linkTopicTypeToOrganizationType,
  unlinkTopicTypeFromOrganizationType,
  updateOrgTypeTopicType
} from 'wasp/client/operations';
import { ChevronDown, ChevronRight, Plus, Trash2, BookOpen, Save, RotateCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

interface Props {
  organizationTypeId: string;
}

export default function OrgTypeTopicTypesPanel({ organizationTypeId }: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Local pending selections: map from OrgTypeTopicType.id -> string[]
  const [pendingSelections, setPendingSelections] = useState<Map<string, string[]>>(new Map());
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const { data: linkedTopicTypes = [], isLoading } = useQuery(getOrgTypeTopicTypes, { organizationTypeId });
  const { data: allTopicTypes = [] } = useQuery(getTopicTypes);

  const linkFn = useAction(linkTopicTypeToOrganizationType);
  const unlinkFn = useAction(unlinkTopicTypeFromOrganizationType);
  const updateFn = useAction(updateOrgTypeTopicType);

  // Sync server state into local pending state (only for records not currently tracked)
  useEffect(() => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      for (const ltt of linkedTopicTypes as any[]) {
        if (!next.has(ltt.id)) {
          next.set(ltt.id, [...ltt.selectedTopicIds]);
        }
      }
      const linkedIds = new Set((linkedTopicTypes as any[]).map((l: any) => l.id));
      for (const key of next.keys()) {
        if (!linkedIds.has(key)) next.delete(key);
      }
      return next;
    });
  }, [linkedTopicTypes]);

  const linkedTopicTypeIds = new Set((linkedTopicTypes as any[]).map((ltt: any) => ltt.topicTypeId));

  const availableTopicTypes = (allTopicTypes as any[]).filter(
    (tt: any) => !linkedTopicTypeIds.has(tt.id) && tt.isActive
  );

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAddTopicType = async (topicTypeId: string) => {
    try {
      const result = await linkFn({ organizationTypeId, topicTypeId }) as any;
      toast({ title: 'Topic category added', description: 'All topic names pre-selected.' });
      setExpandedIds(prev => new Set([...prev, result.id]));
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to add topic category.', variant: 'destructive' });
    }
  };

  const handleRemoveTopicType = async (id: string, label: string) => {
    if (!confirm(`Remove topic category "${label}" from this organization type? All topic name selections will be lost.`)) return;
    try {
      await unlinkFn({ id });
      setPendingSelections(prev => { const next = new Map(prev); next.delete(id); return next; });
      toast({ title: 'Topic category removed', description: `"${label}" unlinked.` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to remove topic category.', variant: 'destructive' });
    }
  };

  const handleToggle = (lttId: string, topicId: string) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      const current = next.get(lttId) ?? [];
      next.set(lttId, current.includes(topicId) ? current.filter(id => id !== topicId) : [...current, topicId]);
      return next;
    });
  };

  const handleSelectAll = (lttId: string, allIds: string[], selectAll: boolean) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      next.set(lttId, selectAll ? [...allIds] : []);
      return next;
    });
  };

  const handleSave = async (ltt: any) => {
    const selected = pendingSelections.get(ltt.id) ?? [];
    setSavingIds(prev => new Set([...prev, ltt.id]));
    try {
      await updateFn({ id: ltt.id, selectedTopicIds: selected });
      toast({ title: 'Saved', description: `Topic name selection updated for "${ltt.topicType.label}".` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save.', variant: 'destructive' });
    } finally {
      setSavingIds(prev => { const next = new Set(prev); next.delete(ltt.id); return next; });
    }
  };

  const handleReset = (ltt: any) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      next.set(ltt.id, [...ltt.selectedTopicIds]);
      return next;
    });
  };

  const isDirty = (ltt: any): boolean => {
    const pending = pendingSelections.get(ltt.id);
    if (!pending) return false;
    const saved: string[] = ltt.selectedTopicIds;
    if (pending.length !== saved.length) return true;
    const savedSet = new Set(saved);
    return pending.some(id => !savedSet.has(id));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Select which topic categories and their topic names are available for this organization type.
      </p>

      {/* Add Topic Category */}
      {availableTopicTypes.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Add Topic Category</p>
          <div className="flex flex-wrap gap-2">
            {availableTopicTypes.map((tt: any) => (
              <button
                key={tt.id}
                onClick={() => handleAddTopicType(tt.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {tt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Linked Topic Categories */}
      {(linkedTopicTypes as any[]).length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
          <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No topic categories linked yet.</p>
          {availableTopicTypes.length > 0 && (
            <p className="text-gray-400 text-xs mt-1">Use the panel above to add topic categories.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {(linkedTopicTypes as any[]).map((ltt: any) => {
            const isExpanded = expandedIds.has(ltt.id);
            const topics = ltt.topicType.topics;
            const allTopicIds = topics.map((t: any) => t.id);
            const selectedIds = pendingSelections.get(ltt.id) ?? ltt.selectedTopicIds;
            const allSelected = topics.length > 0 && selectedIds.length === topics.length;
            const noneSelected = selectedIds.length === 0;
            const dirty = isDirty(ltt);
            const saving = savingIds.has(ltt.id);

            return (
              <div key={ltt.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Category Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                  <button
                    onClick={() => toggleExpand(ltt.id)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="font-medium text-gray-900">{ltt.topicType.label}</span>
                    <span className="text-xs text-gray-400 ml-1">
                      {selectedIds.length} / {topics.length} selected
                    </span>
                    {dirty && (
                      <span className="text-xs text-amber-600 font-medium ml-1">• unsaved</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleRemoveTopicType(ltt.id, ltt.topicType.label)}
                    className="ml-3 inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove
                  </button>
                </div>

                {/* Topics */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                    {topics.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">No active topics in this category.</p>
                    ) : (
                      <>
                        {/* Toolbar */}
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => handleSelectAll(ltt.id, allTopicIds, true)}
                              disabled={allSelected}
                              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                              Select all
                            </button>
                            <button
                              onClick={() => handleSelectAll(ltt.id, allTopicIds, false)}
                              disabled={noneSelected}
                              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                              Deselect all
                            </button>
                          </div>
                          {dirty && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleReset(ltt)}
                                disabled={saving}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                              >
                                <RotateCcw className="w-3 h-3" />
                                Reset
                              </button>
                              <button
                                onClick={() => handleSave(ltt)}
                                disabled={saving}
                                className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                              >
                                <Save className="w-3 h-3" />
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {topics.map((topic: any) => (
                            <label key={topic.id} className="flex items-center gap-3 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(topic.id)}
                                onChange={() => handleToggle(ltt.id, topic.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-800 group-hover:text-gray-900">{topic.name}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



